import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { AlertCircle, ArrowDown, ArrowUp, ChevronsUpDown, Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

type Columns<TData> = ColumnDef<TData, unknown>[];

interface DataTableProps<TData> {
  columns: Columns<TData>;
  data: TData[];
  isLoading?: boolean;
  error?: string | null;
  /** Optional retry handler; renders a "Try again" button in the error state. */
  onRetry?: () => void;
  emptyMessage?: string;
  /** Optional toolbar rendered as its own row above the table card (search, filters, actions). */
  toolbar?: ReactNode;
  /** Extra classes for the outer wrapper (e.g. `h-full` to fill the viewport). */
  className?: string;
  /** Stable row id accessor; falls back to index. */
  getRowId?: (row: TData) => string;
  /** Optional row click handler; makes rows interactive (cursor + hover). */
  onRowClick?: (row: TData) => void;

  // --- server-driven pagination ---
  manualPagination?: boolean;
  pageCount?: number;
  rowCount?: number;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;

  // --- server-driven sorting (wired by the search/sort slice) ---
  manualSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
}

export function DataTable<TData>({
  columns,
  data,
  isLoading = false,
  error = null,
  onRetry,
  emptyMessage = "No records found",
  toolbar,
  className,
  getRowId,
  onRowClick,
  manualPagination = false,
  pageCount,
  rowCount,
  pagination,
  onPaginationChange,
  manualSorting = false,
  sorting,
  onSortingChange,
}: DataTableProps<TData>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
    // Sorting UI only appears once a page wires it up (see the search/sort slice).
    enableSorting: Boolean(onSortingChange),
    manualPagination,
    pageCount: manualPagination ? (pageCount ?? -1) : undefined,
    rowCount,
    manualSorting,
    state: {
      ...(pagination ? { pagination } : {}),
      ...(sorting ? { sorting } : {}),
    },
    onPaginationChange,
    onSortingChange,
  });

  const colCount = columns.length;

  return (
    <div className={cn("flex min-h-0 flex-col gap-4", className)}>
      {toolbar ? <div className="flex-shrink-0">{toolbar}</div> : null}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <Table containerClassName="min-h-0 flex-1 overflow-y-auto">
          <TableHeader className="sticky top-0 z-10 bg-gray-50">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="bg-gray-50 hover:bg-gray-50">
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-900"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === "asc" ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : sorted === "desc" ? (
                            <ArrowDown className="h-3 w-3" />
                          ) : (
                            <ChevronsUpDown className="h-3 w-3 opacity-40" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={colCount} className="py-12 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-gray-400" />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={colCount} className="py-12 text-center text-red-600">
                  <AlertCircle className="mx-auto mb-2 h-6 w-6" />
                  <div className="text-sm">{error}</div>
                  {onRetry ? (
                    <button
                      type="button"
                      onClick={onRetry}
                      className="mt-3 rounded-lg border border-red-200 px-3 py-1.5 font-medium text-red-700 text-sm transition-colors hover:bg-red-50"
                    >
                      Try again
                    </button>
                  ) : null}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={colCount} className="py-12 text-center text-sm text-gray-400">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={onRowClick ? "cursor-pointer" : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {manualPagination && !isLoading && !error ? (
          <DataTablePagination table={table} rowCount={rowCount ?? data.length} />
        ) : null}
      </div>
    </div>
  );
}

function DataTablePagination({ table, rowCount }: { table: any; rowCount: number }) {
  const { pageIndex, pageSize } = table.getState().pagination as PaginationState;
  const pageCount = table.getPageCount();
  const from = rowCount === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, rowCount);

  return (
    <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-500">
      <span>
        {from}–{to} of {rowCount}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          className={cn(
            "rounded-lg border border-gray-200 px-3 py-1.5 transition-colors hover:bg-gray-50",
            "disabled:cursor-not-allowed disabled:opacity-40"
          )}
        >
          Previous
        </button>
        <span className="px-1 text-gray-600">
          Page {pageIndex + 1} of {Math.max(pageCount, 1)}
        </span>
        <button
          type="button"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          className={cn(
            "rounded-lg border border-gray-200 px-3 py-1.5 transition-colors hover:bg-gray-50",
            "disabled:cursor-not-allowed disabled:opacity-40"
          )}
        >
          Next
        </button>
      </div>
    </div>
  );
}
